import React, { useState, useEffect } from 'react';
import axios from 'axios';
import SEO from '../components/SEO';
import { InFeedAd } from '../components/AdSense';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import GuestBanner from '../components/GuestBanner';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Dog,
  Search,
  Loader2,
  Heart,
  Zap,
  Scissors,
  Baby,
  Activity,
  Clock,
  Scale,
  Ruler,
  MapPin,
  Check,
  X,
  ExternalLink
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Breeds = () => {
  const [breeds, setBreeds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [groupFilter, setGroupFilter] = useState('all');
  const [sizeFilter, setSizeFilter] = useState('all');
  const [selectedBreed, setSelectedBreed] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);

  useEffect(() => {
    fetchBreeds();
  }, [groupFilter, sizeFilter]);

  const fetchBreeds = async () => {
    setLoading(true);
    try {
      let url = `${API}/breeds?limit=300&`;
      if (groupFilter !== 'all') url += `group=${groupFilter}&`;
      if (sizeFilter !== 'all') url += `size=${sizeFilter}&`;
      const response = await axios.get(url);
      setBreeds(response.data);
    } catch (error) {
      toast.error('Failed to load breeds');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      fetchBreeds();
      return;
    }
    setLoading(true);
    try {
      const response = await axios.get(`${API}/breeds/search/${searchQuery}`);
      setBreeds(response.data);
    } catch (error) {
      toast.error('Search failed');
    } finally {
      setLoading(false);
    }
  };

  const openBreedDetail = (breed) => {
    setSelectedBreed(breed);
    setDetailOpen(true);
  };

  const groups = [
    { value: 'all', label: 'All Groups' },
    { value: 'Sporting', label: 'Sporting' },
    { value: 'Hound', label: 'Hound' },
    { value: 'Working', label: 'Working' },
    { value: 'Terrier', label: 'Terrier' },
    { value: 'Toy', label: 'Toy' },
    { value: 'Non-Sporting', label: 'Non-Sporting' },
    { value: 'Herding', label: 'Herding' },
    { value: 'Hybrid', label: 'Hybrid / Designer' }
  ];

  const sizes = [
    { value: 'all', label: 'All Sizes' },
    { value: 'small', label: 'Small' },
    { value: 'medium', label: 'Medium' },
    { value: 'large', label: 'Large' },
    { value: 'giant', label: 'Giant' }
  ];

  const getLevelBadge = (level) => {
    const colors = {
      'low': 'bg-green-100 text-green-700',
      'moderate': 'bg-yellow-100 text-yellow-700',
      'high': 'bg-red-100 text-red-700',
      'easy': 'bg-green-100 text-green-700',
      'challenging': 'bg-red-100 text-red-700'
    };
    return colors[level] || 'bg-muted text-muted-foreground';
  };

  const getSizeBadge = (size) => {
    const colors = {
      'small': 'bg-blue-100 text-blue-700',
      'medium': 'bg-purple-100 text-purple-700',
      'large': 'bg-orange-100 text-orange-700',
      'giant': 'bg-red-100 text-red-700'
    };
    return colors[size] || 'bg-muted text-muted-foreground';
  };

  return (
    <div className="min-h-screen bg-background">
      <SEO
        title="Dog Breeds — Full Directory & Breed Finder"
        description="Complete directory of dog breeds with detailed information on temperament, health, grooming, training, and care. Browse by AKC-style groups (Sporting, Hound, Working, Terrier, Toy, Herding, Non-Sporting), size, or search by name."
        keywords={[
          'dog breeds',
          'list of dog breeds',
          'breeds of dogs',
          'dog breed finder',
          'compare dog breeds',
          'dog breed information',
          'sporting dogs',
          'hound dogs',
          'working dogs',
          'herding dogs',
          'terrier breeds',
          'toy dog breeds',
          'non-sporting dogs',
          'hypoallergenic dog breeds',
          'apartment-friendly dogs',
          'family dog breeds',
          'small dog breeds',
          'large dog breeds',
        ]}
        url="/breeds"
      />
      <div className="container mx-auto px-4 py-8">
        {/* Guest Banner */}
        <GuestBanner message="Sign up to save favorite breeds and get personalized recommendations" />
        
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-heading font-medium mb-2">Breed Database</h1>
          <p className="text-muted-foreground text-lg">
            Explore {breeds.length > 0 ? `${breeds.length} ` : ''}dog breeds and their characteristics
          </p>
        </div>

        {/* Search and Filters */}
        <div className="flex flex-col sm:flex-row gap-4 mb-8">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search breeds..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              className="pl-10 input-field"
              data-testid="breed-search"
            />
          </div>
          <Select value={groupFilter} onValueChange={setGroupFilter}>
            <SelectTrigger className="w-full sm:w-48">
              <SelectValue placeholder="Group" />
            </SelectTrigger>
            <SelectContent>
              {groups.map(g => (
                <SelectItem key={g.value} value={g.value}>{g.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={sizeFilter} onValueChange={setSizeFilter}>
            <SelectTrigger className="w-full sm:w-40">
              <SelectValue placeholder="Size" />
            </SelectTrigger>
            <SelectContent>
              {sizes.map(s => (
                <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Breeds Grid */}
        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : breeds.length === 0 ? (
          <Card className="text-center py-16">
            <CardContent>
              <Dog className="w-16 h-16 mx-auto mb-4 text-muted-foreground" />
              <h3 className="font-heading text-xl mb-2">No breeds found</h3>
              <p className="text-muted-foreground">Try adjusting your search or filters</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {breeds.map((breed) => (
              <Card 
                key={breed.id}
                className="card-interactive cursor-pointer overflow-hidden"
                onClick={() => openBreedDetail(breed)}
                data-testid={`breed-card-${breed.id}`}
              >
                <div className="aspect-video bg-muted relative overflow-hidden">
                  {breed.image_url ? (
                    <img 
                      src={breed.image_url}
                      alt={breed.name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Dog className="w-12 h-12 text-muted-foreground/30" />
                    </div>
                  )}
                  <Badge className={`absolute top-2 right-2 ${getSizeBadge(breed.size)}`}>
                    {breed.size}
                  </Badge>
                </div>
                <CardContent className="p-4">
                  <h3 className="font-heading text-lg font-medium mb-1">{breed.name}</h3>
                  <p className="text-sm text-muted-foreground mb-3">{breed.group} Group</p>
                  
                  <div className="flex flex-wrap gap-1 mb-3">
                    {breed.temperament.slice(0, 3).map((t, i) => (
                      <Badge key={i} variant="outline" className="text-xs">{t}</Badge>
                    ))}
                  </div>
                  
                  <div className="flex items-center gap-4 text-xs text-muted-foreground">
                    <div className="flex items-center gap-1" title="Energy Level">
                      <Zap className="w-3 h-3" />
                      <span className="capitalize">{breed.energy_level}</span>
                    </div>
                    <div className="flex items-center gap-1" title="Life Expectancy">
                      <Clock className="w-3 h-3" />
                      <span>{breed.life_expectancy}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Ad placement below breed grid */}
        {!loading && breeds.length > 0 && (
          <InFeedAd slot="landingInFeed" className="mt-8" />
        )}

        {/* Breed Detail Dialog */}
        <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
          <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
            {selectedBreed && (
              <>
                <DialogHeader>
                  <DialogTitle className="font-heading text-2xl">{selectedBreed.name}</DialogTitle>
                </DialogHeader>
                
                <div className="space-y-6 mt-4">
                  {/* Image */}
                  {selectedBreed.image_url && (
                    <div className="aspect-video rounded-lg overflow-hidden">
                      <img 
                        src={selectedBreed.image_url}
                        alt={selectedBreed.name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  )}
                  
                  {/* Basic Info */}
                  <div className="flex flex-wrap gap-2">
                    <Badge className={getSizeBadge(selectedBreed.size)}>{selectedBreed.size}</Badge>
                    <Badge variant="outline">{selectedBreed.group} Group</Badge>
                    {selectedBreed.akc_recognized && (
                      <Badge variant="outline" className="text-green-600">AKC Recognized</Badge>
                    )}
                  </div>
                  
                  {/* Description */}
                  <p className="text-muted-foreground">{selectedBreed.description}</p>
                  
                  {/* Temperament */}
                  <div>
                    <h4 className="font-medium mb-2">Temperament</h4>
                    <div className="flex flex-wrap gap-2">
                      {selectedBreed.temperament.map((t, i) => (
                        <Badge key={i} variant="secondary">{t}</Badge>
                      ))}
                    </div>
                  </div>
                  
                  {/* Physical Characteristics */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <div className="flex items-center gap-2 text-sm">
                        <Scale className="w-4 h-4 text-muted-foreground" />
                        <span className="text-muted-foreground">Weight:</span>
                        <span className="font-medium">{selectedBreed.weight_range}</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm">
                        <Ruler className="w-4 h-4 text-muted-foreground" />
                        <span className="text-muted-foreground">Height:</span>
                        <span className="font-medium">{selectedBreed.height_range}</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm">
                        <Clock className="w-4 h-4 text-muted-foreground" />
                        <span className="text-muted-foreground">Life Span:</span>
                        <span className="font-medium">{selectedBreed.life_expectancy}</span>
                      </div>
                      {selectedBreed.origin && (
                        <div className="flex items-center gap-2 text-sm">
                          <MapPin className="w-4 h-4 text-muted-foreground" />
                          <span className="text-muted-foreground">Origin:</span>
                          <span className="font-medium">{selectedBreed.origin}</span>
                        </div>
                      )}
                    </div>
                    
                    <div className="space-y-2">
                      <div className="flex items-center gap-2 text-sm">
                        <Zap className="w-4 h-4 text-muted-foreground" />
                        <span className="text-muted-foreground">Energy:</span>
                        <Badge className={getLevelBadge(selectedBreed.energy_level)} variant="secondary">
                          {selectedBreed.energy_level}
                        </Badge>
                      </div>
                      <div className="flex items-center gap-2 text-sm">
                        <Activity className="w-4 h-4 text-muted-foreground" />
                        <span className="text-muted-foreground">Exercise:</span>
                        <Badge className={getLevelBadge(selectedBreed.exercise_needs)} variant="secondary">
                          {selectedBreed.exercise_needs}
                        </Badge>
                      </div>
                      <div className="flex items-center gap-2 text-sm">
                        <Scissors className="w-4 h-4 text-muted-foreground" />
                        <span className="text-muted-foreground">Grooming:</span>
                        <Badge className={getLevelBadge(selectedBreed.grooming_needs)} variant="secondary">
                          {selectedBreed.grooming_needs}
                        </Badge>
                      </div>
                    </div>
                  </div>
                  
                  {/* Compatibility */}
                  <div>
                    <h4 className="font-medium mb-2">Compatibility</h4>
                    <div className="grid grid-cols-2 gap-2">
                      <div className="flex items-center gap-2 text-sm">
                        <Baby className="w-4 h-4 text-muted-foreground" />
                        <span>Good with children:</span>
                        {selectedBreed.good_with_children ? (
                          <Check className="w-4 h-4 text-green-500" />
                        ) : (
                          <X className="w-4 h-4 text-red-500" />
                        )}
                      </div>
                      <div className="flex items-center gap-2 text-sm">
                        <Dog className="w-4 h-4 text-muted-foreground" />
                        <span>Good with dogs:</span>
                        {selectedBreed.good_with_other_dogs ? (
                          <Check className="w-4 h-4 text-green-500" />
                        ) : (
                          <X className="w-4 h-4 text-red-500" />
                        )}
                      </div>
                    </div>
                  </div>
                  
                  {/* Additional Info */}
                  <div className="grid grid-cols-2 gap-4 pt-4 border-t">
                    <div className="text-sm">
                      <span className="text-muted-foreground">Shedding:</span>{' '}
                      <span className="font-medium capitalize">{selectedBreed.shedding_level}</span>
                    </div>
                    <div className="text-sm">
                      <span className="text-muted-foreground">Trainability:</span>{' '}
                      <span className="font-medium capitalize">{selectedBreed.trainability}</span>
                    </div>
                  </div>

                  {/* Source Citation */}
                  {selectedBreed.source_url && (
                    <div className="pt-3 border-t text-xs text-muted-foreground" data-testid="breed-citation">
                      <span>Source: </span>
                      <a 
                        href={selectedBreed.source_url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-primary hover:underline inline-flex items-center gap-1"
                      >
                        {selectedBreed.source || 'Wikipedia'}
                        <ExternalLink className="w-3 h-3" />
                      </a>
                      {selectedBreed.image_credit && selectedBreed.image_url && (
                        <span className="ml-2">| Image: {selectedBreed.image_credit}</span>
                      )}
                    </div>
                  )}
                </div>
              </>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default Breeds;
